import { WarningAmber } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Booking, BookingState } from '@thro-types/leihbar';

import { useFormikContext } from 'formik';
import { KeyboardEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useArticlesWithConflictBookingsQuery } from 'api/hooks/leihbar';
import { translations } from 'locales/i18n';
import { formatToDateAndTime } from 'utils/dates';

interface Props {
  setDisabled: Function;
  isDisabled: boolean;
  booking: Booking | undefined;
  productId: number;
}
export function BookingInformation(props: Props) {
  const { t } = useTranslation();

  const isSkuDisabled = props.booking?.state === BookingState.COLLECTED;

  const { errors, touched, handleBlur, values, setFieldValue } = useFormikContext<Booking>();

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.preventDefault();
  };

  const { data: articles, isLoading } = useArticlesWithConflictBookingsQuery(
    !!(values.startTime && values.endTime),
    props.productId,
    {
      params: {
        productId: props.productId,
        startTime: new Date(values.startTime).toISOString(),
        endTime: new Date(values.endTime).toISOString(),
        articleId: values.article?.id,
        id: values.id
      }
    }
  );
  const actualArticle = articles?.find(article => article.id === values.article?.id);

  useEffect(() => {
    if (!isLoading && actualArticle?.bookings && actualArticle?.bookings?.length > 0) {
      props.setDisabled(true);
    } else {
      props.setDisabled(false);
    }
  }, [actualArticle?.bookings, isLoading, props]);

  return (
    <Grid container>
      <Grid item xs={12} marginBottom={1}>
        <Alert severity="info">
          <AlertTitle>
            {t(translations.pages.leihbar.booking.warnings.availableArticle.title())}
          </AlertTitle>
          <Grid container item xs={11} gap={2}>
            <Typography fontSize={14}>
              {t(translations.pages.leihbar.booking.warnings.availableArticle.messagePart1())}
            </Typography>
            <Typography fontSize={14}>
              {t(translations.pages.leihbar.booking.warnings.availableArticle.messagePart2())}
            </Typography>
            <Typography fontSize={14}>
              {t(translations.pages.leihbar.booking.warnings.availableArticle.messagePart3())}
            </Typography>
          </Grid>
        </Alert>
      </Grid>
      <Grid item xs={12} marginTop={2} gap={2}>
        <FormControl fullWidth style={{ paddingBottom: '1em' }}>
          <FormLabel>{t(translations.pages.leihbar.article.create.articleId())}</FormLabel>
          <Tooltip
            title={
              isSkuDisabled ? t(translations.pages.leihbar.booking.tooltips.skuDisabled()) : ''
            }
          >
            <Select
              MenuProps={{ sx: { maxHeight: '20em' } }}
              size="small"
              value={values.article?.id}
              defaultValue={values.article?.id}
              disabled={isSkuDisabled}
              onChange={async e => {
                setFieldValue('article.id', e.target.value);
              }}
            >
              {articles && articles.length ? (
                articles.map(article => {
                  return (
                    <MenuItem value={article.id}>
                      {article.sku}
                      {article.bookings && !isLoading && article.bookings.length > 0 ? (
                        <WarningAmber fontSize="small" />
                      ) : (
                        <></>
                      )}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem value={values.article?.id}>
                  {values.article?.sku || `${t(translations.common.loading())}...`}
                </MenuItem>
              )}
            </Select>
          </Tooltip>
        </FormControl>
        <FormControl fullWidth style={{ paddingBottom: '1em' }}>
          <FormLabel>{t(translations.pages.leihbar.shoppingCart.pickUpTime())}</FormLabel>
          <DateTimePicker
            renderInput={props => (
              <TextField
                {...props}
                id="startTime"
                name="startTime"
                variant="outlined"
                error={touched.startTime && Boolean(errors.startTime)}
                helperText={touched.startTime && String(errors.startTime ?? '')}
                onBlur={handleBlur}
                size="small"
              />
            )}
            value={values.startTime}
            onChange={value => {
              setFieldValue('startTime', value);
            }}
            mask="__.__.____ __:__"
            minDateTime={new Date()}
            inputFormat="dd.MM.yyyy HH:mm"
            ampm={false}
            ampmInClock={false}
            minutesStep={15}
            InputProps={{ onKeyDown }}
          />
        </FormControl>
        <FormControl fullWidth style={{ paddingBottom: '1em' }}>
          <FormLabel>{t(translations.pages.leihbar.shoppingCart.returnTime())}</FormLabel>
          <DateTimePicker
            renderInput={props => (
              <TextField
                {...props}
                id="endTime"
                name="endTime"
                variant="outlined"
                error={touched.endTime && Boolean(errors.endTime)}
                helperText={touched.endTime && String(errors.endTime ?? '')}
                onBlur={handleBlur}
                size="small"
              />
            )}
            value={values.endTime}
            onChange={value => {
              setFieldValue('endTime', value);
            }}
            mask="__.__.____ __:__"
            minDateTime={values.startTime}
            inputFormat="dd.MM.yyyy HH:mm"
            ampm={false}
            ampmInClock={false}
            minutesStep={15}
            InputProps={{ onKeyDown: e => onKeyDown(e) }}
          />
        </FormControl>
      </Grid>
      {actualArticle?.bookings && actualArticle?.bookings?.length > 0 && (
        <Grid item>
          <Alert severity="warning" sx={{ gap: 1 }}>
            <AlertTitle marginBottom={1}>
              {t(translations.pages.leihbarOpeningHoursSettings.vacation.conflictingBookings())}
            </AlertTitle>
            <Grid container flexDirection={'column'} gap={1}>
              {actualArticle.bookings.map(booking => (
                <li>
                  {`Buchung ${booking.id}: ${formatToDateAndTime(
                    new Date(booking.startTime)
                  )} - ${formatToDateAndTime(new Date(booking.endTime))}`}
                </li>
              ))}
            </Grid>
          </Alert>
        </Grid>
      )}
    </Grid>
  );
}
